import React, { useMemo } from 'react';

import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd/es/table/InternalTable';
import styled from 'styled-components';

import DownCaretIcon from 'common/Icons/DownCaretIcon';
import Dropdown from 'components/lib/Dropdown';
import {
  SimpleListActionButtonContent,
  SimpleListActionsButton,
  SimpleListTableEmptyState,
} from 'components/lib/SimpleListModalComponents';
import { SimpleListTable } from 'components/lib/SimpleListTable';
import Tooltip from 'components/lib/Tooltip';
import {
  COMMUNITY_CONTACT_DELIVERY_METHOD_LABELS,
  COMMUNITY_CONTACT_SOURCE_LABELS,
  COMMUNITY_CONTACT_TYPE_LABELS,
  CommunityContactSourceEnum,
} from 'constants/community';
import { CommunityContactType } from 'types/communities';

const BaseTable = (props: TableProps<CommunityContactType>) => {
  return <Table<CommunityContactType> {...props} />;
};

const StyledTable = styled(BaseTable)`
  ${SimpleListTable}
  &&&&&& {
    width: 100%;

    .ant-table-thead > tr > th {
      padding-left: 10px !important;
      padding-right: 10px !important;
    }

    .ant-table-tbody > tr > td {
      padding: 8px 10px !important;
    }

    .ant-table-body {
      max-height: 260px !important;
      overflow-y: auto !important;
    }

    .actions-hover-reveal {
      visibility: hidden;
    }

    .ant-table-cell-row-hover {
      .actions-hover-reveal {
        visibility: visible;
      }
    }
  }
`;

const ValueText = styled.span`
  display: inline-block;
  color: var(--text-primary);
`;

const NoWrapValueText = styled(ValueText)`
  white-space: nowrap;
`;

const NoWrapHeader = styled.span`
  white-space: nowrap;
`;

const ContactCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContactPrimary = styled.span`
  color: var(--text-primary);
  font-family: var(--font-bold);
  word-break: break-word;
`;

const ContactSecondary = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
  word-break: break-word;
`;

const MutedText = styled.span`
  display: inline-block;
  color: var(--text-secondary);
`;

const BooleanCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  width: 100%;
`;

const MatchingIcon = styled(CheckOutlined)`
  color: var(--positive-green);
  font-size: 16px;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SourcePill = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: default;
  width: fit-content;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--line-gray);
  font-size: 12px;
  font-family: var(--font-bold);
  background: var(--lighter-gray);
  white-space: nowrap;
`;

const Container = styled.div`
  display: flex;
  flex-grow: 1;
`;

const getContactActorDisplayName = (actor: CommunityContactType['created_by']) => {
  if (!actor) {
    return '';
  }

  if (typeof actor === 'string') {
    return actor;
  }

  const fullName = actor.full_name?.trim();
  if (fullName) {
    return fullName;
  }

  const combinedName = [actor.first_name, actor.last_name].filter(Boolean).join(' ').trim();
  return combinedName || actor.email?.trim() || '';
};

type Props = {
  communityContacts: CommunityContactType[];
  deletingContactId: number | null;
  isFetchingCommunityContacts: boolean;
  isLoadingCommunityContacts: boolean;
  onDelete: (item: CommunityContactType) => void;
  onEdit: (item: CommunityContactType) => void;
};

const ManageContactsTable = ({
  communityContacts,
  deletingContactId,
  isFetchingCommunityContacts,
  isLoadingCommunityContacts,
  onDelete,
  onEdit,
}: Props) => {
  const columns = useMemo<ColumnsType<CommunityContactType>>(
    () => [
      {
        title: 'Contact',
        dataIndex: 'name',
        key: 'contact',
        width: 220,
        render: (_: string, item) => {
          const name = item.name?.trim();
          const phone = item.phone?.trim();

          return (
            <ContactCell>
              {name ? (
                <>
                  <ContactPrimary>{name}</ContactPrimary>
                  {item.email ? <ContactSecondary>{item.email}</ContactSecondary> : null}
                </>
              ) : (
                <ContactPrimary>{item.email || phone || 'Community contact'}</ContactPrimary>
              )}
              {phone && (name || item.email) ? <ContactSecondary>{phone}</ContactSecondary> : null}
            </ContactCell>
          );
        },
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 165,
        render: (value: number) =>
          COMMUNITY_CONTACT_TYPE_LABELS[value] ? (
            <NoWrapValueText>{COMMUNITY_CONTACT_TYPE_LABELS[value]}</NoWrapValueText>
          ) : (
            <MutedText>-</MutedText>
          ),
      },
      {
        title: <NoWrapHeader>Delivery Method</NoWrapHeader>,
        dataIndex: 'delivery_method',
        key: 'delivery_method',
        width: 135,
        render: (value: number) =>
          COMMUNITY_CONTACT_DELIVERY_METHOD_LABELS[value] ? (
            <NoWrapValueText>{COMMUNITY_CONTACT_DELIVERY_METHOD_LABELS[value]}</NoWrapValueText>
          ) : (
            <MutedText>-</MutedText>
          ),
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        width: 120,
        render: (_: number, item) => {
          const actorDisplayName = getContactActorDisplayName(item.created_by);

          if (!COMMUNITY_CONTACT_SOURCE_LABELS[item.source]) {
            return <MutedText>-</MutedText>;
          }

          const sourcePill = <SourcePill>{COMMUNITY_CONTACT_SOURCE_LABELS[item.source]}</SourcePill>;

          if (item.source !== CommunityContactSourceEnum.MANUALLY_ADDED || !actorDisplayName) {
            return sourcePill;
          }

          return <Tooltip title={`Added by ${actorDisplayName}`}>{sourcePill}</Tooltip>;
        },
      },
      {
        title: <NoWrapHeader>Shop Request Contact</NoWrapHeader>,
        dataIndex: 'shop_request_contact_enabled',
        key: 'shop_request_contact_enabled',
        width: 155,
        render: (value: boolean) => <BooleanCell>{value ? <MatchingIcon /> : null}</BooleanCell>,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        align: 'center',
        render: (_, item) => {
          const isDeleting = deletingContactId === item.id;

          return (
            <ActionsCell className="actions-hover-reveal">
              <Dropdown
                placement="bottomRight"
                menu={{
                  items: [
                    {
                      label: 'Edit',
                      key: 'edit',
                      disabled: isDeleting,
                      onClick: () => onEdit(item),
                    },
                    {
                      label: 'Delete',
                      key: 'delete',
                      disabled: isDeleting,
                      onClick: () => onDelete(item),
                    },
                  ],
                }}
              >
                <SimpleListActionsButton disabled={isDeleting}>
                  <SimpleListActionButtonContent>
                    <span>{isDeleting ? 'Loading' : 'Actions'}</span>
                    {isDeleting ? <LoadingOutlined /> : <DownCaretIcon />}
                  </SimpleListActionButtonContent>
                </SimpleListActionsButton>
              </Dropdown>
            </ActionsCell>
          );
        },
      },
    ],
    [deletingContactId, onDelete, onEdit]
  );

  return (
    <Container>
      <StyledTable
        rowKey="id"
        columns={columns}
        dataSource={communityContacts}
        loading={isLoadingCommunityContacts || isFetchingCommunityContacts}
        pagination={false}
        scroll={{ y: 260 }}
        locale={{
          emptyText: <SimpleListTableEmptyState>No contacts added yet.</SimpleListTableEmptyState>,
        }}
      />
    </Container>
  );
};

export default ManageContactsTable;
